import * as React from 'react';
import styled, { DefaultTheme, css } from 'styled-components';
import { memoizeFunction, isNumber, isString } from '@agilepoint/utils-common';
import { excludeKeys, generateGridTemplate, setNativeValue, useChooseRef } from '../../utils';
import { ITextboxProps } from './textbox-props';
import { Icon } from '../icon';
import { TextboxState } from './theme';
import { selectThemeSemantics } from '../../styling';
import { typographyPresets } from '../typography';
import { FlexBox } from '../flex-box';

const MainIcon = styled(Icon) <{ $order?: number, $disabled?: boolean }>`
    transition: fill .15s ease-in-out;
    order: ${({ $order }) => $order};
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
`;

const ClearIcon = styled(MainIcon).attrs({
    iconName: 'ap-close.outline',
    iconSize: '12'
})`
    cursor: pointer;
`;

const IconWrapper = styled(FlexBox) <{ $order: number }>`
    order: ${({ $order }) => $order};
`;

const getTextboxStyleByState = memoizeFunction((state: TextboxState) => {
    const getStyles = (theme: DefaultTheme) => {
        const { textbox: { colors } } = theme;
        return colors[state];
    };
    return css`
        background-color: ${({ theme }) => getStyles(theme).backgroundColor};
        border-color: ${({ theme }) => getStyles(theme).borderColor};
        color: ${({ theme }) => getStyles(theme).foregroundColor};

        ${MainIcon} {
            fill: ${({ theme }) => getStyles(theme).iconColor};
        };

        ${ClearIcon} {
            fill: ${({ theme }) => getStyles(theme).clearIconColor};
        }
    `;
});

const getTextboxStyle = memoizeFunction((state: TextboxState) => ({ disabled, error }: ITextboxProps) => getTextboxStyleByState(disabled ? 'disabled' : (error ? 'error' : state)));

const StyledInput = styled.input.attrs({
    className: String('ap-textbox')
}) <{ $order: number }>`
  font-family: inherit;
  ${typographyPresets.textStyles.small};
  box-sizing: border-box;
  border: none;
  order: ${({ $order }) => $order};
  border-radius: ${selectThemeSemantics(
    ({ borderRadius }) => borderRadius.curvedSmall
)};
  background: inherit;
  color: inherit;
  cursor: inherit;
  height: 100%;
  width: 100%;
  :focus {
    outline: 0;
  }
`;

const Wrapper = styled.div<{ $gap: string, $gridTemplate: string }>`
    display: inline-grid;
    align-items: center;
    grid-template: ${({ $gridTemplate }) => $gridTemplate};
    grid-gap: ${({ $gap }) => `${$gap}px`};
`;

const TextboxBase = React.memo((props: ITextboxProps) => {
    const {
        className, label, elementRef, iconName, iconSize = '16', disabled,
        error, hidden, type, onValueChange, onChange, title, gap = '16', iconPosition = 'left',
        allowToClear, onClear, value, defaultValue, clearIconTitle, maxLength, onClick, ...rest
    } = props;
    const textboxProps = excludeKeys(rest, 'children');
    const textInputRef = useChooseRef(elementRef);
    const [isClearIconHidden, hideClearIcon] = React.useState(!(defaultValue || value));
    const refOrder = iconPosition === 'left' ? 0 : 2;

    React.useEffect(() => {
        if (allowToClear && value !== undefined) {
            hideClearIcon(!value);
        }
    }, [allowToClear, value]);

    React.useEffect(() => {
        let targetValue = (value || defaultValue || '');
        targetValue = (
            isString(targetValue)
                ? targetValue
                : (isNumber(targetValue)
                    ? targetValue.toString()
                    : targetValue.join()
                )
        );
        if (onValueChange && maxLength && (targetValue.length > maxLength)) {
            onValueChange(targetValue.substring(0, maxLength));
        }
    }, [value, defaultValue, maxLength, onValueChange]);

    const _onChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { value: targetValue } = e.target;
        allowToClear && hideClearIcon(value !== undefined ? !value : !targetValue);
        if (onValueChange !== undefined) {
            onValueChange(targetValue);
        }
        if (onChange !== undefined) {
            onChange(e);
        }
    }, [value, allowToClear, onValueChange, onChange]);

    const setFocusForTextbox = React.useCallback(() => {
        textInputRef.current?.focus();
    }, [textInputRef]);

    const onClearIconClick = React.useCallback((e: React.MouseEvent) => {
        hideClearIcon(!value);
        if (onClear) {
            onClear(e);
        }
        if (textInputRef.current === null) {
            return;
        }
        const changeEvent = new Event('input', {
            bubbles: true
        });

        setNativeValue(textInputRef.current, '');
        textInputRef.current.dispatchEvent(changeEvent);
    }, [onClear, value, textInputRef]);

    const shouldHideClearIcon = (!allowToClear || isClearIconHidden || disabled);

    const gridTemplate = generateGridTemplate(!!iconName, !shouldHideClearIcon, iconPosition !== 'left');

    return hidden ? null : (
        <Wrapper $gap={gap} className={className} title={title} onClick={setFocusForTextbox}
            $gridTemplate={gridTemplate}>
            <>{iconName && <MainIcon $order={refOrder - 0} iconName={iconName} iconSize={iconSize} onClick={onClick} $disabled={disabled} />}</>
            <StyledInput
                {...textboxProps}
                $order={Math.abs(refOrder - 1)}
                value={value}
                defaultValue={defaultValue}
                ref={textInputRef}
                type={type}
                maxLength={maxLength}
                aria-label={label}
                disabled={disabled}
                onChange={_onChange}
                onClick={onClick} />
            {
                shouldHideClearIcon
                    ? null
                    : (
                        <IconWrapper $order={Math.abs(refOrder - 2)} alignItems="center" title={clearIconTitle}>
                            <ClearIcon onClick={onClearIconClick} />
                        </IconWrapper>
                    )
            }
        </Wrapper>
    );
});

TextboxBase.displayName = 'TextboxBase';

export const Textbox = styled(TextboxBase).attrs({
    className: String('ap-textbox-wrapper')
})`
    height: 40px;
    min-width: 50px;
    box-sizing: border-box;
    overflow: hidden;
    transition: all 0.15s ease-in-out;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'text')};
    border-radius: ${({ theme: { textbox: { composites } } }) => composites.borderRadius};
    border-style: ${({ theme: { textbox: { composites } } }) => composites.borderStyle};
    border-width: ${({ theme: { textbox: { composites } } }) => composites.borderWidth};
    padding: ${selectThemeSemantics(({ gap }) => `0 ${gap.xl}`)};
    ${getTextboxStyle('default')}
    &:hover {
        ${getTextboxStyle('hovered')};
    }
    &:focus-within, :active {
        ${getTextboxStyle('focussed')};
    };
`;

Textbox.defaultProps = {
    type: 'text'
};

Textbox.displayName = 'Textbox';
